"use client";

import { ArrowRight, ShieldAlert, Rat, Bug, Sparkles, Search, Radio, Flame, ShieldCheck, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  target: string;
  icon: React.ComponentType<{
    className?: string;
    size?: number;
  }>;
}
const services: ServiceItem[] = [{
  id: "general-pest-control",
  title: "General Pest Control",
  description: "Target common household invaders with exterior barrier treatments and dependable ongoing defense.",
  target: "#pest-control",
  icon: ShieldCheck
}, {
  id: "rodent-control",
  title: "Rodent Control",
  description: "Exterior perimeter treatment, entry point treatment, minor exclusion of small cracks and holes, and monitoring and follow-up as your plan requires.",
  target: "/RodentControlPage",
  icon: Rat
}, {
  id: "ant-control",
  title: "Ant Control",
  description: "Eliminate ant trails and foraging colonies around your foundation before they move inside.",
  target: "/AntControlPage",
  icon: Bug
}, {
  id: "cockroach-control",
  title: "Cockroach Control",
  description: "Address cockroach harborage areas with targeted treatments designed for high-humidity living zones.",
  target: "/CockroachControlPage",
  icon: ShieldAlert
}, {
  id: "spider-control",
  title: "Spider Control",
  description: "Clear exterior webs, sweep eaves, and establish protective boundaries against spiders.",
  target: "/SpiderControlPage",
  icon: Sparkles
}, {
  id: "flea-tick-control",
  title: "Flea & Tick Control",
  description: "Protect your yard perimeter and transition zones from biting fleas and ticks throughout the warm months.",
  target: "/FleaAndTickControlPage",
  icon: Search
}, {
  id: "mosquito-control",
  title: "Mosquito Control",
  description: "Reduce biting mosquito populations with targeted misting of shaded foliage and outdoor resting zones.",
  target: "/MosquitoControlPage",
  icon: Radio
}, {
  id: "wasp-control",
  title: "Wasp & Stinging Insect Control",
  description: "Remove active wasp nests and treat overhangs where stinging insects build colonies.",
  target: "/WaspStingingInsectControlPage",
  icon: Zap
}, {
  id: "entry-point-treatments",
  title: "Entry Point Treatments",
  description: "Targeted treatments around doors, windows, vents, eaves and other exterior entry points, plus minor sealing of small cracks and holes.",
  target: "#entry-point-treatments",
  icon: Flame
}];
export default function ServicesOverview() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="services-overview" id="services-overview" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-8 bg-primary" />
            <span className="font-mono text-xs uppercase tracking-widest text-primary">
              Full Spectrum Coverage
            </span>
            <span className="block h-px w-8 bg-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-4 tracking-tight">
            Our Services
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-sans">
            Explore our specialized pest management services built for homes throughout Durham and the Triangle.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {services.map((service, index) => {
          const Icon = service.icon;
          return <Card key={service.id} data-index={index} className="group relative flex flex-col h-full rounded-md border border-border bg-card shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-1">
                <CardHeader className="pt-6 pb-3">
                  <div className="size-12 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                    <Icon size={24} />
                  </div>
                  <CardTitle className="text-xl font-bold font-default text-foreground tracking-tight">
                    <h3>{service.title}</h3>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow pb-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
                <CardFooter className="pt-2 pb-6">
                  <Button asChild variant="ghost" className="p-0 h-auto font-mono text-xs tracking-wider uppercase text-foreground hover:text-primary hover:bg-transparent">
                    <Link to={service.target} className="inline-flex items-center gap-1.5 font-semibold">
                      Learn More
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}